"use client";

import { useState, useEffect, useMemo } from "react";
import Step2AddressInput from "./Step2AddressInput";
import Step2LocationTypePicker from "./Step2LocationTypePicker";
import AddressDetailInput from "./AddressDetailInput";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useOrderStore } from "@/store/useOrderStore";
import { checkServiceArea, getServiceAreas } from "@/lib/service-area";
import { orderRequiresInsidePickup, orderRequiresInsideDelivery } from "@/lib/disassembly";
import { calcLocationAdjustments } from "@/lib/price-calculator";
import { useAdminMode } from "@/hooks/useAdminMode";
import { cn } from "@/lib/utils";
import type { AddressInfo, LocationType } from "@/types/address";
import type { AddressInfo as AddressInfoStore } from "@/types/order";

interface Props {
  onComplete: () => void;
  skipServiceAreaCheck?: boolean;
  /** 견적 문의 흐름: 물품 정보가 없어 위치별 금액을 계산할 수 없으므로 라벨만 표시 */
  quoteMode?: boolean;
  /** 서비스 지역 밖일 때 견적 문의 모드로 전환 */
  onSwitchToQuote?: () => void;
}

interface AreaError {
  city: string;
  district: string;
}

const SIDE_GUIDANCE: Record<"origin" | "destination", Record<LocationType, string>> = {
  origin: {
    INSIDE: "상세주소 안에서 물품을 수거합니다.",
    DOOR: "상세주소 문앞에서 물품을 수거합니다.",
    PARKING:
      "물품의 수거 위치와 주차 위치 및 방법을 알려주세요. 주차위치와 물품수거위치 사이에 계단이 없어야해요. 물품 도난 및 분실의 우려가 없는 곳이거나 물품을 건네주실 분이 계셔야 합니다.",
  },
  destination: {
    INSIDE: "상세주소 안까지 물품을 배송합니다.",
    DOOR: "상세주소 문앞까지 물품을 배송합니다.",
    PARKING:
      "물품의 배송 위치와 주차 위치 및 방법을 알려주세요. 주차위치와 물품배송위치 사이에 계단이 없어야해요. 물품 도난 및 분실의 우려가 없는 곳이거나 물품을 건네받으신 분이 계셔야 합니다.",
  },
};

const INSIDE_PICKUP_MESSAGE = "분해를 위해서는 집안 수거만 가능해요.";
const INSIDE_DELIVERY_MESSAGE = "조립을 위해서는 집안 배송만 가능해요.";

/** floor 미선택 상태 sentinel (드롭다운 placeholder 값과 동일) */
const FLOOR_UNSET = -99;

/** 엘리베이터 가능/불가능 버튼 공통 스타일 (위치 선택 버튼과 동일 톤) */
const EV_BTN_BASE = "flex-1 py-2.5 rounded-lg text-sm font-medium border transition-colors";
const EV_BTN_ACTIVE = "bg-[#1A3A5C] text-white border-[#1A3A5C]";
const EV_BTN_IDLE = "bg-white text-gray-700 border-gray-300 hover:border-[#1A3A5C]";

/**
 * 상세주소에서 운반 계단 층수(=건물 층 - 1)를 추정.
 * - "호" 앞 숫자 우선, 없으면 마지막 숫자 그룹을 호수로 간주.
 * - 호수 → 건물 층: 3자리 이상이면 백의 자리 이상(304→3, 1502→15), 1~2자리면 그 값.
 * - 계단 층수 = max(0, 층-1), 드롭다운 최대(5)로 캡.
 * 예) "304호"→2, "103동 401호"→3, "211-205"→1, "101호"→0
 */
function parseStairsFromDetail(detail: string): number {
  if (!detail) return 0;
  let unit: number | null = null;
  const hoMatch = detail.match(/(\d+)\s*호/);
  if (hoMatch) {
    unit = Number(hoMatch[1]);
  } else {
    const nums = detail.match(/\d+/g);
    if (nums && nums.length > 0) unit = Number(nums[nums.length - 1]);
  }
  if (unit == null || !Number.isFinite(unit)) return 0;
  const buildingFloor = unit >= 100 ? Math.floor(unit / 100) : unit;
  const stairs = Math.max(0, buildingFloor - 1);
  return Math.min(stairs, 5);
}

/** 운반 계단 층수 드롭다운 옵션 (Step2ElevatorSelect 와 동일 셋) */
const FLOOR_OPTIONS: { value: string; label: string }[] = [
  { value: "0", label: "없음 (3칸 이하)" },
  { value: "0.5", label: "0.5층 (10칸 이하)" },
  { value: "1", label: "1층" },
  { value: "1.5", label: "1.5층" },
  { value: "2", label: "2층" },
  { value: "2.5", label: "2.5층" },
  { value: "3", label: "3층" },
  { value: "3.5", label: "3.5층" },
  { value: "4", label: "4층" },
  { value: "4.5", label: "4.5층" },
  { value: "5", label: "5층" },
  { value: "-10", label: "모름 (배송후정산)" },
];

export default function Step2Address({ onComplete, skipServiceAreaCheck = false, quoteMode = false, onSwitchToQuote }: Props) {
  const { origin, destination, setOrigin, setDestination, items, itemInfo, quoteId } = useOrderStore();
  const locationLocked = !!quoteId; // 확정 견적 링크: 위치 변경 불가 (가격 고정)
  const { isAdmin } = useAdminMode();
  const insidePickupRequired = orderRequiresInsidePickup(items, itemInfo);
  const insideDeliveryRequired = orderRequiresInsideDelivery(items, itemInfo);
  const [attempted, setAttempted] = useState(false);
  const [originAreaError, setOriginAreaError] = useState<AreaError | null>(null);
  const [destAreaError, setDestAreaError] = useState<AreaError | null>(null);
  const [areaText, setAreaText] = useState("");

  useEffect(() => {
    if (skipServiceAreaCheck) return;
    getServiceAreas().then((areas) => {
      if (areas.length === 0) return;
      const districts = areas.map((a) => a.district.replace("구", "")).join(", ");
      setAreaText(
        `현재는 서울시 ${areas.length}개 구(${districts})에서만 서비스를 제공하고 있습니다. 빠르게 서비스 지역을 확장할 수 있도록 하겠습니다.`,
      );
    });
  }, [skipServiceAreaCheck]);

  // 분해/조립이 추가된 직후, 이미 비-INSIDE 가 선택돼 있다면 자동 초기화 (해당 side 만)
  useEffect(() => {
    if (insidePickupRequired && origin?.locationType && origin.locationType !== "INSIDE") {
      setOrigin({ ...origin, locationType: null });
    }
    if (insideDeliveryRequired && destination?.locationType && destination.locationType !== "INSIDE") {
      setDestination({ ...destination, locationType: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insidePickupRequired, insideDeliveryRequired]);

  // 기존 AddressInfo → AddressInfo (input 컴포넌트가 기대하는 모양)
  const convertToAddressInfo = (info: typeof origin): AddressInfo | null => {
    if (!info) return null;
    return {
      address: info.address,
      addressDetail: info.addressDetail,
      zonecode: (info as AddressInfo).zonecode || "",
      roadAddress: (info as AddressInfo).roadAddress || info.address,
      jibunAddress: (info as AddressInfo).jibunAddress || "",
      buildingName: (info as AddressInfo).buildingName || "",
      lat: info.lat,
      lng: info.lng,
      hasElevator: info.hasElevator,
      floor: info.floor,
      carryHelp: info.carryHelp,
      locationType: info.locationType,
      parkingInfo: info.parkingInfo,
    };
  };

  const buildNextSide = (
    info: AddressInfo,
    prev: AddressInfoStore | null,
  ): AddressInfoStore => {
    // 새 주소 선택 시 EV·층수는 미선택 상태로 시작 (상세주소 입력 → EV 버튼 → 층수 순서로 노출).
    // EV 선택 시점에 floor 를 채운다(가능→0.5 / 불가능→상세주소 파싱).
    const base: AddressInfoStore = {
      address: info.address,
      addressDetail: info.addressDetail,
      lat: info.lat || 0,
      lng: info.lng || 0,
      hasElevator: false,
      floor: FLOOR_UNSET,
      carryHelp: prev?.carryHelp,
      locationType: prev?.locationType ?? null,
      parkingInfo: prev?.parkingInfo,
    };
    return base;
  };

  const handleOriginChange = async (info: AddressInfo) => {
    if (skipServiceAreaCheck) {
      setOriginAreaError(null);
    } else {
      const result = await checkServiceArea(info.address);
      if (!result.available) {
        setOriginAreaError({ city: result.city, district: result.district });
      } else {
        setOriginAreaError(null);
      }
    }
    setOrigin(buildNextSide(info, origin));
  };

  const handleDestinationChange = async (info: AddressInfo) => {
    if (skipServiceAreaCheck) {
      setDestAreaError(null);
    } else {
      const result = await checkServiceArea(info.address);
      if (!result.available) {
        setDestAreaError({ city: result.city, district: result.district });
      } else {
        setDestAreaError(null);
      }
    }
    setDestination(buildNextSide(info, destination));
  };

  const originInfo = convertToAddressInfo(origin);
  const destInfo = convertToAddressInfo(destination);

  // 위치 보정 미리보기 (현재 선택된 모드 기반)
  const locationAdjustments = useMemo(() => {
    const allItems = [
      ...items.map((i) => ({
        uid: i.uid,
        basePrice: i.basePrice,
        locationInsideExtra: i.locationInsideExtra ?? 0,
        locationParkingDiscount: i.locationParkingDiscount ?? 0,
      })),
      ...(itemInfo.itemId
        ? [
            {
              uid: "_current",
              basePrice: itemInfo.basePrice,
              locationInsideExtra: itemInfo.locationInsideExtra ?? 0,
              locationParkingDiscount: itemInfo.locationParkingDiscount ?? 0,
            },
          ]
        : []),
    ];
    return calcLocationAdjustments(
      allItems,
      origin?.locationType,
      destination?.locationType,
    );
  }, [items, itemInfo, origin?.locationType, destination?.locationType]);

  // ── 검증 ──
  const checkSideComplete = (
    info: typeof origin,
    areaError: AreaError | null,
    sideAdjustment: { surcharge: number; discount: number },
  ): boolean => {
    if (!info?.address || !info?.lat || !info?.lng) return false;
    if (areaError) return false;
    if (!info.locationType) return false;
    if (info.locationType === "PARKING") {
      return !!info.parkingInfo && info.parkingInfo.trim().length > 0;
    }
    // INSIDE / DOOR
    if (!info.addressDetail) return false;
    if (info.floor === undefined || info.floor === -99) return false;
    void sideAdjustment;
    return true;
  };

  const originComplete = checkSideComplete(origin, originAreaError, {
    surcharge: locationAdjustments.originSurcharge,
    discount: locationAdjustments.originDiscount,
  });
  const destComplete = checkSideComplete(destination, destAreaError, {
    surcharge: locationAdjustments.destSurcharge,
    discount: locationAdjustments.destDiscount,
  });
  const isComplete = originComplete && destComplete;

  // ── side panel 렌더링 ──
  const renderSidePanel = (
    side: "origin" | "destination",
    info: typeof origin,
    setInfo: typeof setOrigin,
    sideAdjustment: { surcharge: number; discount: number },
  ) => {
    if (!info?.locationType) return null;
    const guidance = SIDE_GUIDANCE[side][info.locationType];

    const surchargeLabel = side === "origin" ? "집안수거 할증" : "집안배송 할증";
    const discountLabel = side === "origin" ? "주차장수거 할인" : "주차장배송 할인";

    // ── PARKING: 안내 + 할인 라벨 + 주차 정보 (기존 방식) ──
    if (info.locationType === "PARKING") {
      return (
        <div className="space-y-3">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800 whitespace-pre-line">
              {guidance.replace(/\.$/, "")}{" "}
              <span className="font-semibold text-emerald-700">
                ({quoteMode || sideAdjustment.discount <= 0
                  ? discountLabel
                  : `-${sideAdjustment.discount.toLocaleString()}원`})
              </span>
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-sm text-gray-600">
              주차 위치 &amp; 진입 방법<span className="text-red-500 ml-0.5">*</span>
            </label>
            <Textarea
              placeholder="예: 지하2층 주차장 102동 1,2호라인 앞, 정문에서 세대 호출"
              value={info.parkingInfo || ""}
              onChange={(e) => setInfo({ ...info, parkingInfo: e.target.value })}
              rows={3}
              className={cn(
                attempted && !info.parkingInfo && "border-red-500 ring-2 ring-red-200",
              )}
            />
            {attempted && !info.parkingInfo && (
              <p className="text-sm text-red-500">주차 위치 &amp; 진입 방법을 입력해주세요</p>
            )}
          </div>
        </div>
      );
    }

    // ── INSIDE / DOOR: 점진적 노출 (가이드+상세주소 → EV 버튼 → 운반계단 층수) ──
    const hasDetail = !!info.addressDetail && info.addressDetail.trim().length > 0;
    const evChosen = info.floor !== FLOOR_UNSET;
    // INSIDE 는 가이드 문구 끝에 집안수거 할증 표기를 괄호로 덧붙인다 (금액 알면 금액, 모르면 라벨)
    const surchargeText =
      quoteMode || sideAdjustment.surcharge <= 0
        ? surchargeLabel
        : `+${sideAdjustment.surcharge.toLocaleString()}원`;
    return (
      <div className="space-y-3">
        {/* 가이드 문구 — 주차장 가이드와 동일 색·크기 (blue, text-sm). INSIDE 는 집안수거 할증 표기(원래 색/굵기)를 괄호로 포함.
            운반계단 층수 가이드가 나오면(EV 선택 후) 바탕·테두리 제거 + 기본(회색) 텍스트로 눈에 띄지 않게 처리 */}
        <div
          className={cn(
            "rounded-lg",
            evChosen ? "px-1" : "bg-blue-50 border border-blue-200 p-3",
          )}
        >
          <p
            className={cn(
              "text-sm whitespace-pre-line",
              evChosen ? "text-gray-500" : "text-blue-800",
            )}
          >
            {info.locationType === "INSIDE" ? (
              <>
                {guidance.replace(/\.$/, "")}{" "}
                <span className={evChosen ? undefined : "font-semibold text-amber-700"}>
                  ({surchargeText})
                </span>
              </>
            ) : (
              guidance
            )}
          </p>
        </div>

        <AddressDetailInput
          value={info.addressDetail || ""}
          onChange={(v) => setInfo({ ...info, addressDetail: v })}
          invalid={attempted && !info.addressDetail}
        />

        {/* 상세주소 입력 후 → 엘리베이터 가능/불가능 버튼 (체크박스 아님) */}
        {hasDetail && (
          <div className="space-y-1.5">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setInfo({ ...info, hasElevator: true, floor: 0.5 })}
                className={cn(EV_BTN_BASE, evChosen && info.hasElevator ? EV_BTN_ACTIVE : EV_BTN_IDLE)}
              >
                엘리베이터 이용 가능함
              </button>
              <button
                type="button"
                onClick={() => setInfo({ ...info, hasElevator: false, floor: parseStairsFromDetail(info.addressDetail || "") })}
                className={cn(EV_BTN_BASE, evChosen && !info.hasElevator ? EV_BTN_ACTIVE : EV_BTN_IDLE)}
              >
                없거나 너무 작음
              </button>
            </div>
            {attempted && !evChosen && (
              <p className="text-sm text-red-500">엘리베이터 이용 가능 여부를 선택해주세요</p>
            )}
          </div>
        )}

        {/* EV 선택 후 → 운반계단 층수 가이드 + 드롭다운 (가능=0.5 기본 / 불가능=상세주소 파싱값) */}
        {hasDetail && evChosen && (
          <div className="space-y-2">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800 whitespace-pre-line">
                1층이거나 엘리베이터가 있어도 차량에서 운반 위치까지 계단이 있을 경우 계단 층수를 선택해주세요.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-600 whitespace-nowrap">
                운반 계단 층수<span className="text-red-500 ml-0.5">*</span>
              </label>
              <select
                value={String(info.floor)}
                onChange={(e) => setInfo({ ...info, floor: Number(e.target.value) })}
                className={cn(
                  "flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1A3A5C]",
                  attempted && info.floor === FLOOR_UNSET && "border-red-500 ring-2 ring-red-200",
                )}
              >
                <option value={String(FLOOR_UNSET)} disabled>
                  물품 운반 경로에 있는 계단 층수를 선택해주세요
                </option>
                {FLOOR_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {areaText && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
          {areaText}
        </div>
      )}
      <div>
        <p className="text-sm text-gray-500">주소를 입력한 뒤 수거/배송 위치를 선택해주세요</p>
      </div>

      {/* 출발지 */}
      <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-3 h-3 rounded-full bg-orange-500" />
          <span className="font-semibold text-[#E8723A]">출발지</span>
        </div>

        <Step2AddressInput
          label=""
          color="#E8723A"
          value={originInfo}
          onChange={handleOriginChange}
          attempted={attempted}
          hideDetail
        />

        {originAreaError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
            <p className="text-sm text-red-700">
              죄송합니다. {originAreaError.city} {originAreaError.district}는 아직 정식 서비스 지역이 아닙니다. 견적 문의로 요청해주시면 일정과 물품에 따라 견적을 안내 드리도록 하겠습니다.
            </p>
            {onSwitchToQuote && (
              <Button
                type="button"
                variant="outline"
                className="w-full border-red-300 text-red-700 hover:bg-red-100"
                onClick={onSwitchToQuote}
              >
                견적 문의
              </Button>
            )}
          </div>
        )}

        {origin?.address && !originAreaError && (
          <>
            <Step2LocationTypePicker
              side="origin"
              value={origin.locationType}
              onChange={(t) => setOrigin({ ...origin, locationType: t })}
              disabledNonInsideReason={insidePickupRequired ? INSIDE_PICKUP_MESSAGE : null}
              attempted={attempted}
              locked={locationLocked}
            />
            {renderSidePanel("origin", origin, setOrigin, {
              surcharge: locationAdjustments.originSurcharge,
              discount: locationAdjustments.originDiscount,
            })}
          </>
        )}
      </div>

      {/* 구분선 */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-gray-400 text-lg">↓</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      {/* 도착지 */}
      <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-3 h-3 rounded-full bg-[#1A3A5C]" />
          <span className="font-semibold text-[#1A3A5C]">도착지</span>
        </div>

        <Step2AddressInput
          label=""
          color="#1A3A5C"
          value={destInfo}
          onChange={handleDestinationChange}
          attempted={attempted}
          hideDetail
        />

        {destAreaError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
            <p className="text-sm text-red-700">
              죄송합니다. {destAreaError.city} {destAreaError.district}는 아직 정식 서비스 지역이 아닙니다. 견적 문의로 요청해주시면 일정과 물품에 따라 견적을 안내 드리도록 하겠습니다.
            </p>
            {onSwitchToQuote && (
              <Button
                type="button"
                variant="outline"
                className="w-full border-red-300 text-red-700 hover:bg-red-100"
                onClick={onSwitchToQuote}
              >
                견적 문의
              </Button>
            )}
          </div>
        )}

        {destination?.address && !destAreaError && (
          <>
            <Step2LocationTypePicker
              side="destination"
              value={destination.locationType}
              onChange={(t) => setDestination({ ...destination, locationType: t })}
              disabledNonInsideReason={insideDeliveryRequired ? INSIDE_DELIVERY_MESSAGE : null}
              attempted={attempted}
              locked={locationLocked}
            />
            {renderSidePanel("destination", destination, setDestination, {
              surcharge: locationAdjustments.destSurcharge,
              discount: locationAdjustments.destDiscount,
            })}
          </>
        )}
      </div>

      {isComplete && (
        <div className="p-3 bg-green-50 rounded-lg text-center">
          <p className="text-sm text-green-700 font-medium">✅ 출발지 · 도착지 입력 완료</p>
        </div>
      )}

      <Button
        onClick={() => {
          if (!isAdmin && !isComplete) {
            setAttempted(true);
            return;
          }
          onComplete();
        }}
        className={cn("w-full", !isAdmin && !isComplete && "bg-gray-300 hover:bg-gray-400 text-gray-700")}
        size="lg"
      >
        다음 단계로
      </Button>
    </div>
  );
}
